import { motion } from "framer-motion";
import { ArrowRight, Sparkles, ShieldCheck, FileText, Star, Award } from "lucide-react";
import { HERO_VIDEO, HERO_FALLBACK } from "../lib/constants";

const trust = [
  { icon: ShieldCheck, label: "Garantie décennale" },
  { icon: FileText, label: "Devis gratuit" },
  { icon: Star, label: "Avis clients 5 étoiles" },
  { icon: Award, label: "+25 ans d'expérience" },
];

const HERO_HEADLINE_ANIM = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: "easeOut" },
};

const HERO_TRUST_ANIM = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { delay: 0.4, duration: 1 },
};

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-end overflow-hidden bg-black"
    >
      {/* Background video */}
      <div className="absolute inset-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={HERO_FALLBACK}
          className="w-full h-full object-cover kenburns"
          data-testid="hero-video"
        >
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/80 via-transparent to-transparent" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 md:pb-32 w-full">
        <motion.div {...HERO_HEADLINE_ANIM}>
          <div className="eyebrow mb-6 inline-flex items-center gap-2" data-testid="hero-eyebrow">
            <Sparkles size={12} />
            <span>Valenciennes — Nord (59)</span>
          </div>

          <h1
            className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-light leading-[0.95] mb-8 max-w-4xl"
            data-testid="hero-headline"
          >
            Votre façade
            <br />
            mérite l&apos;<em className="text-[#FF5A00] not-italic font-normal">excellence</em>.
          </h1>

          <p
            className="text-lg md:text-xl text-neutral-300 max-w-2xl leading-relaxed mb-10"
            data-testid="hero-subheadline"
          >
            Spécialiste du ravalement de façade, enduits projetés, rejointoiement de briques,
            imitation pierre, peinture façade et rénovation extérieure.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mb-16">
            <a
              href="#configurateur"
              data-testid="hero-cta-configurator"
              className="btn-primary text-base"
            >
              Visualiser votre nouvelle façade <ArrowRight size={18} />
            </a>
            <a
              href="#rdv-devis"
              data-testid="hero-cta-quote"
              className="btn-primary text-base"
              style={{ background: "transparent", border: "1px solid #FF5A00", color: "#FF5A00" }}
            >
              Réserver mon devis gratuit <ArrowRight size={18} />
            </a>
            <a
              href="#realisations"
              data-testid="hero-cta-realisations"
              className="btn-ghost text-base"
            >
              Voir nos réalisations
            </a>
          </div>

          <motion.div
            {...HERO_TRUST_ANIM}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-3xl"
            data-testid="trust-badges"
          >
            {trust.map((t) => (
              <div
                key={t.label}
                className="glass rounded-2xl px-4 py-4 flex items-center gap-3"
                data-testid={`trust-badge-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="w-9 h-9 rounded-full bg-[#FF5A00]/15 flex items-center justify-center text-[#FF5A00] shrink-0">
                  <t.icon size={16} />
                </div>
                <span className="text-[13px] md:text-sm text-neutral-200 leading-tight">
                  {t.label}
                </span>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
          Scroll
        </span>
        <div className="w-px h-12 bg-gradient-to-b from-[#FF5A00] to-transparent" />
      </div>
    </section>
  );
}
